// Складовата карта на продукт в „Наличности": хедър (продукт, склад, наличност
// и стойност, минимум), табове Движения · Партиди · Замени и периодът (общ за
// Движения и Замени, по подразбиране последните 30 дни). На десктоп е панел
// вдясно от тесния списък (× затваря), под 1024 px — лист на цял екран с
// „← Наличности". Табът и периодът остават при смяна на продукта с ↑/↓ —
// сравняваш няколко продукта за един и същ период. Табовете: ←/→ между тях.
// По избор (справката „Продадени продукти", която НЕ е по склад): периодът и
// видът движение, с които картата се отваря, избор на склада в хедъра,
// етикетът на „назад" на телефон и лента над табовете (`summary`).
// „Наличности" към минала дата (`asOfDay`): наличността в хедъра е към края на
// деня, стойността — по текущите цени (така и пише), партидите — към датата;
// периодът по подразбиране свършва на нея (виж `StockReport`).
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Spin } from "antd";
import { CloseOutlined, LeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { StockCardDocumentType } from "../../../../api/inventoryStockCard";
import { useLots, useStockAsOf } from "../../../../hooks/useInventory";
import { useStockCard } from "../../../../hooks/useInventoryStockCard";
import { formatQty } from "../../components/qty";
import { formatMoney } from "../../components/numbers";
import { StatusPill } from "../../components/list/ListBits";
import { DeletedProductChip } from "../../components/product/DeletedProductChip";
import { PeriodPicker } from "../../components/period/PeriodPicker";
import { periodToParams, type Period } from "../../components/period/periodPresets";
import type { StockRow } from "../components/stockShared";
import { StockCardMovements } from "./StockCardMovements";
import { StockCardLots } from "./StockCardLots";
import { StockCardSubstitutions } from "./StockCardSubstitutions";
import { defaultCardPeriod, shortDateFormat, type CardTypeFilter } from "./stockCardModel";
import { useStockCardDocuments } from "./useStockCardDocuments";

type CardTab = "movements" | "lots" | "substitutions";
const TABS: CardTab[] = ["movements", "lots", "substitutions"];

type Props = {
  row: StockRow;
  warehouseId: number;
  warehouseName: string;
  variant: "pane" | "sheet";
  onClose: () => void;
  initialPeriod?: Period; // по подразбиране — последните 30 дни
  initialMovementType?: CardTypeFilter; // чипът в „Движения" (ако го има в периода)
  warehousePicker?: ReactNode; // на мястото на името на склада
  backLabel?: string; // „← …" на телефон; по подразбиране „Наличности"
  summary?: ReactNode; // лента под хедъра, над табовете
  asOfDay?: string | null; // YYYY-MM-DD — редът е към края на този ден (иначе — сега)
};

export const StockCardPanel = ({
  row,
  warehouseId,
  warehouseName,
  variant,
  onClose,
  initialPeriod,
  initialMovementType,
  warehousePicker,
  backLabel,
  summary,
  asOfDay = null,
}: Props) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<CardTab>("movements");
  const [period, setPeriod] = useState<Period>(() => initialPeriod ?? defaultCardPeriod());
  const params = useMemo(
    () => ({ warehouseId, productId: row.productId, ...periodToParams(period) }),
    [warehouseId, row.productId, period],
  );
  const cardQuery = useStockCard(params);
  const card = cardQuery.data;
  // Партидите: текущите или — при минала дата — отворените към края на деня
  const currentLotsQuery = useLots(asOfDay ? null : warehouseId, row.productId);
  const asOfQuery = useStockAsOf(warehouseId, row.productId, asOfDay);
  const lotsData = asOfDay ? asOfQuery.data?.lots : currentLotsQuery.data;
  const lotsLoading = asOfDay ? asOfQuery.isLoading : currentLotsQuery.isLoading;
  const lots = lotsData ?? [];
  const documents = useStockCardDocuments();
  const tabRefs = useRef<Partial<Record<CardTab, HTMLButtonElement | null>>>({});
  const backRef = useRef<HTMLButtonElement | null>(null);

  // Листът на телефон/таблет покрива справката — фокусът влиза в него
  useEffect(() => {
    if (variant === "sheet") backRef.current?.focus();
  }, [variant]);

  // Редът на движение или на замяна → документът му (замяната сочи родителя)
  const openDocument = (target: { documentType: StockCardDocumentType; documentId: number; date: string }) =>
    documents.openDocument({ type: target.documentType, id: target.documentId, date: target.date });

  const onTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const step = event.key === "ArrowRight" ? 1 : -1;
    const next = TABS[(TABS.indexOf(tab) + step + TABS.length) % TABS.length];
    setTab(next);
    tabRefs.current[next]?.focus();
  };

  const tabLabels: Record<CardTab, { label: string; count: number | undefined }> = {
    movements: { label: t("inventoryPage.scTabMovements"), count: card?.entries.length },
    lots: { label: t("inventoryPage.scTabLots"), count: lotsData?.length },
    substitutions: { label: t("inventoryPage.scTabSubstitutions"), count: card?.substitutions.length },
  };
  const unit = row.unit ?? "";

  return (
    <section
      data-stock-card
      aria-label={t("inventoryPage.scCardAria", { name: row.name })}
      className={`flex h-full min-h-0 flex-1 flex-col bg-panel ${variant === "pane" ? "border-l border-shade-2" : ""}`}
    >
      <header className="flex shrink-0 flex-col gap-2 border-b border-shade-2 px-4 pb-3 pt-3">
        {variant === "sheet" && (
          <button
            ref={backRef}
            type="button"
            onClick={onClose}
            className="flex cursor-pointer items-center gap-1.5 self-start rounded-md border-none bg-transparent p-0 text-[13px] leading-[normal] text-body outline-none hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--color-brand)]"
          >
            <LeftOutlined className="text-[11px]" />
            {backLabel ?? t("inventoryPage.stockReport")}
          </button>
        )}
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            {warehousePicker ? (
              <div className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 text-[11px] font-semibold uppercase leading-[normal] tracking-[0.1em] text-muted">
                  {t("inventoryPage.scTitle")}
                </span>
                {warehousePicker}
              </div>
            ) : (
              <div className="truncate text-[11px] font-semibold uppercase leading-[normal] tracking-[0.1em] text-muted">
                {t("inventoryPage.scTitle")} · {warehouseName}
              </div>
            )}
            <div className="mt-1 flex min-w-0 items-center gap-2">
              <h2 className="m-0 truncate text-lg font-bold leading-[1.2] tracking-[-0.01em] text-ink">{row.name}</h2>
              {/* Изтрит в организацията: историята се чете, но го няма в търсачките */}
              {row.deleted && <DeletedProductChip />}
            </div>
          </div>
          {variant === "pane" && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t("inventoryPage.scClose")}
              title={`${t("inventoryPage.scClose")} (Esc)`}
              className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted outline-none hover:bg-shade-1 focus-visible:shadow-[0_0_0_2px_var(--color-brand)]"
            >
              <CloseOutlined className="text-sm" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] leading-[normal] text-body">
          <span>
            {asOfDay
              ? t("inventoryPage.scInStockAsOf", { date: dayjs(asOfDay).format("DD.MM.YYYY") })
              : t("inventoryPage.scInStock")}{" "}
            <span
              className={`font-sheet text-[15px] font-bold tabular-nums ${
                row.bucket === "under" && row.minQty ? "text-danger-ink" : "text-brand-deep-ink"
              }`}
            >
              {formatQty(row.quantity, row.decimals)}
            </span>{" "}
            {unit}
          </span>
          <span>
            {asOfDay ? t("inventoryPage.stockGrossCurrentPrices") : t("inventoryPage.colGross")}{" "}
            <span className="font-sheet font-semibold tabular-nums text-ink">{formatMoney(row.sumWithVat)}</span>
          </span>
          {row.minQty != null && (
            <span>{t("inventoryPage.stockMinLine", { qty: `${formatQty(row.minQty, row.decimals)} ${unit}`.trim() })}</span>
          )}
          {row.bucket === "under" && !!row.minQty && (
            <StatusPill tone="danger">{t("inventoryPage.underMin")}</StatusPill>
          )}
        </div>
      </header>
      {summary}

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-shade-2 px-4 py-2">
        <div role="tablist" aria-label={t("inventoryPage.scTitle")} onKeyDown={onTabKeyDown} className="flex gap-0.5">
          {TABS.map((key) => {
            const active = key === tab;
            return (
              <button
                key={key}
                ref={(element) => {
                  tabRefs.current[key] = element;
                }}
                type="button"
                role="tab"
                id={`stock-card-tab-${key}`}
                aria-selected={active}
                aria-controls="stock-card-tabpanel"
                tabIndex={active ? 0 : -1}
                onClick={() => setTab(key)}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border-none px-3 py-1.5 text-[13.5px] leading-[normal] outline-none focus-visible:shadow-[0_0_0_2px_var(--color-brand)] ${
                  active ? "bg-brand-tint font-semibold text-brand-deep-ink" : "bg-transparent text-body hover:bg-shade-1"
                }`}
              >
                {tabLabels[key].label}
                {tabLabels[key].count != null && (
                  <span className="font-sheet text-xs opacity-80">{tabLabels[key].count}</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex-1" />
        {tab !== "lots" && <PeriodPicker value={period} onChange={setPeriod} />}
      </div>

      <div
        id="stock-card-tabpanel"
        role="tabpanel"
        aria-labelledby={`stock-card-tab-${tab}`}
        className="flex min-h-0 flex-1 flex-col"
      >
        {tab === "movements" && (
          <StockCardMovements
            card={card}
            isLoading={cardQuery.isLoading}
            isRefreshing={cardQuery.isPlaceholderData}
            period={period}
            productId={row.productId}
            productName={row.name}
            warehouseName={warehouseName}
            decimals={row.decimals}
            onOpenDocument={openDocument}
            defaultType={initialMovementType}
          />
        )}
        {tab === "lots" && (
          <StockCardLots lots={lots} isLoading={lotsLoading} decimals={row.decimals} unit={row.unit} />
        )}
        {tab === "substitutions" &&
          (card ? (
            <StockCardSubstitutions
              substitutions={card.substitutions}
              decimals={row.decimals}
              unit={row.unit}
              dateFormat={shortDateFormat(period)}
              onOpenDocument={openDocument}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center p-10">
              <Spin />
            </div>
          ))}
      </div>
      {documents.modals}
    </section>
  );
};

export default StockCardPanel;
